import FarmOutReservationPanel from '../farm-outs/FarmOutReservationPanel';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import EntityDrawer from '../ui/EntityDrawer';
import Tabs from '../ui/Tabs';
import LoadingSpinner from '../LoadingSpinner';
import { adminAPI, paymentRequestAPI } from '../../services/api';
import AccountTypeahead, {
  AccountTypeaheadOption,
} from '../account/AccountTypeahead';
import CustomerTypeahead, {
  CustomerTypeaheadOption,
} from '../customer/CustomerTypeahead';
import AsyncTypeahead, { AsyncTypeaheadOption } from '../ui/AsyncTypeahead';
import { QuoteTermsSummary } from '../quotes/QuoteTermsEditor';
import RecordContactSnapshotsPanel from '../contacts/RecordContactSnapshotsPanel';
import { RecordContactSnapshot } from '../../types/contactSnapshot';
import CreateInvoiceModal from '../CreateInvoiceModal';
import RecordInvoicesPanel from '../invoices/RecordInvoicesPanel';
import InvoiceDrawerHost from '../invoices/InvoiceDrawerHost';
import { Invoice } from '../../types/invoice';
import { describeDepositTiming } from '../../utils/billingPresentation';

type ReservationDrawerTab = 'overview' | 'itinerary' | 'vehicles' | 'drivers' | 'billing' | 'invoices' | 'payments';
type ManualPaymentTarget = 'balance_invoice' | 'grouped_request';
type ManualPaymentDetails = {
  paymentMethod: string;
  note?: string;
};

type ReservationInvoice = {
  id: string;
  invoice_number?: string | null;
  description?: string | null;
  memo?: string | null;
  status?: string | null;
  total?: number | string | null;
  amount_paid?: number | string | null;
  due_date?: string | null;
  issued_at?: string | null;
  paid_at?: string | null;
  payment_intent_id?: string | null;
  stripe_invoice_id?: string | null;
  payment_url?: string | null;
  metadata?: Record<string, any> | null;
};

type ReservationBillingInvoice = {
  id: string;
  invoiceNumber?: string | null;
  kind?: 'deposit' | 'balance' | 'other' | string;
  status?: string | null;
  total?: string | number | null;
  amountPaid?: string | number | null;
  amountDue?: string | number | null;
  dueDate?: string | null;
  issuedAt?: string | null;
  paidAt?: string | null;
  paymentUrl?: string | null;
  stripeInvoiceId?: string | null;
  customerEmail?: string | null;
  description?: string | null;
};

type ReservationBillingPaymentRequest = {
  id: string;
  status?: string | null;
  requestTotal?: string | number | null;
  requestTotalCents?: number | null;
  allocatedTotal?: string | number | null;
  allocatedTotalCents?: number | null;
  paidAt?: string | null;
  expiresAt?: string | null;
  publicUrl?: string | null;
  invoiceIds?: string[];
};

type ReservationBillingSummary = {
  contractTotal?: string | number | null;
  subtotal?: string | number | null;
  serviceCharge?: string | number | null;
  gratuity?: string | number | null;
  tax?: string | number | null;
  depositRequired?: string | number | null;
  depositAmount?: string | number | null;
  depositPaid?: string | number | null;
  depositStatus?: string | null;
  depositPaymentUrl?: string | null;
  depositInvoice?: ReservationBillingInvoice | null;
  balanceInvoiced?: string | number | null;
  balancePaid?: string | number | null;
  balanceRemaining?: string | number | null;
  balanceStatus?: string | null;
  balanceInvoice?: ReservationBillingInvoice | null;
  balancePaymentUrl?: string | null;
  balancePaymentRequest?: ReservationBillingPaymentRequest | null;
  paymentStatus?: string | null;
  depositPayment?: Record<string, any> | null;
  nextAction?: string | null;
  allowedActions?: Record<string, boolean>;
  invoices?: ReservationBillingInvoice[];
};

type InvoiceSearchOption = AsyncTypeaheadOption & {
  invoiceId: string;
};

type ReservationDetail = {
  id: string;
  reservationRef?: string;
  sourceType?: string;
  customerReference?: string | null;
  splitBillingConfig?: Record<string, any> | null;
  status?: string;
  assignmentStatus?: string;
  driverAssignmentStatus?: string;
  driverReadiness?: {
    required?: number;
    assigned?: number;
    blockingIssues?: string[];
    warnings?: string[];
  };
  readiness?: {
    blockingIssues?: string[];
    warnings?: string[];
    groups?: Array<{
      key: string;
      label: string;
      state: string;
      blockingIssues?: string[];
      warnings?: string[];
      completedAt?: string | null;
    }>;
  };
  dispatchStatus?: string;
  dispatchChecklist?: Record<string, any>;
  dispatchWarnings?: string[];
  auditEvents?: Array<Record<string, any>>;
  paymentStatus?: string;
  lifecycleLabel?: string;
  startDate?: string;
  endDate?: string;
  totalPrice?: string | number;
  depositAmount?: string | number;
  import_batch_id?: string | null;
  createdAt?: string;
  updatedAt?: string;
  vehicle?: {
    id?: string;
    make?: string;
    model?: string;
    licensePlate?: string;
  } | null;
  user?: {
    id?: string;
    email?: string;
    firstName?: string | null;
    lastName?: string | null;
    kycStatus?: string | null;
    account?: {
      id: string;
      name: string;
    } | null;
  } | null;
  customer?: {
    id?: string | null;
    email?: string | null;
    name?: string | null;
  } | null;
  account?: {
    id?: string | null;
    name?: string | null;
  } | null;
  tripDetails?: {
    passenger_name?: string | null;
    customer_name?: string | null;
    customer_email?: string | null;
    customer_company_name?: string | null;
    pickup_time?: string | null;
    pickup_location?: string | null;
    dropoff_location?: string | null;
    affiliate?: string | null;
    vehicle_type?: string | null;
    vehicle_label?: string | null;
    driver_name?: string | null;
    payment_method?: string | null;
    imported_status?: string | null;
    imported_rate?: string | null;
    imported_cost?: string | null;
    quote_id?: string | null;
    quote_number?: string | null;
    stops?: any[];
    pickup_address?: any;
    dropoff_address?: any;
  } | null;
  vehicleRequirements?: any[];
  vehicleAssignments?: any[];
  driverAssignments?: any[];
  quoteSource?: {
    id?: string | null;
    number?: string | null;
    termsSnapshot?: Record<string, any> | null;
    pricingSnapshot?: Record<string, any> | null;
    depositPaymentRequestId?: string | null;
    depositPaymentUrl?: string | null;
    depositInvoiceId?: string | null;
    depositInvoiceNumber?: string | null;
  } | null;
  relatedInvoices?: ReservationInvoice[];
  relatedPayments?: ReservationInvoice[];
  billingSummary?: ReservationBillingSummary;
  contactSnapshots?: RecordContactSnapshot[];
};

type ReservationDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  reservationId: string | null;
  onReservationUpdated?: () => void;
};

function formatDollars(value?: string | number | null) {
  const amount = Number(value || 0);
  const normalized = Number.isFinite(amount) ? amount : 0;
  return `$${normalized.toFixed(2)}`;
}

function formatCents(value?: string | number | null) {
  const amount = Number(value || 0);
  const normalized = Number.isFinite(amount) ? amount : 0;
  return `$${(normalized / 100).toFixed(2)}`;
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }

  return parsed.toLocaleString();
}

function formatLabel(value?: string | null) {
  const normalized = (value || '').trim();
  if (!normalized) {
    return 'N/A';
  }

  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatNextBillingAction(value?: string | null) {
  switch ((value || '').toLowerCase()) {
    case 'pay_deposit':
      return 'Collect deposit';
    case 'create_balance_invoice':
      return 'Create balance invoice';
    case 'finalize_balance_invoice':
      return 'Finalize balance invoice';
    case 'await_balance_payment':
      return 'Awaiting balance payment';
    case 'collect_balance':
      return 'Collect balance';
    case 'collect_remaining_balance':
      return 'Collect remaining balance';
    case 'paid_in_full':
      return 'Paid in full';
    default:
      return 'Review billing';
  }
}

function formatBillingState(value?: string | null) {
  switch ((value || '').toLowerCase()) {
    case 'paid':
      return 'Paid in full';
    case 'awaiting_payment':
      return 'Awaiting payment';
    case 'ready_to_pay':
      return 'Ready to send';
    case 'draft':
      return 'Draft balance invoice';
    case 'not_ready':
      return 'Not ready';
    case 'not_invoiced':
      return 'Balance not invoiced';
    default:
      return 'Review billing';
  }
}

async function copyTextToClipboard(value: string, message: string) {
  await navigator.clipboard?.writeText(value).catch(() => undefined);
  toast.success(message);
}

function getPublicUrlFromResponse(response: any) {
  return (
    response?.data?.public_url ||
    response?.data?.publicUrl ||
    response?.data?.payment_url ||
    response?.data?.paymentUrl ||
    null
  );
}

function getStatusClass(status?: string | null) {
  const normalized = (status || '').trim().toLowerCase();

  switch (normalized) {
    case 'confirmed':
      return 'bg-success-container text-success-on-container';
    case 'pending':
    case 'draft':
    case 'awaiting_payment':
      return 'bg-warning-container text-warning-on-container';
    case 'cancelled':
      return 'bg-error-container text-error-on-container';
    case 'open':
    case 'ready_to_pay':
      return 'bg-info-container text-info-on-container';
    case 'paid':
    case 'completed':
      return 'bg-success-container text-success-on-container';
    case 'active':
      return 'bg-primary/15 text-primary';
    default:
      return 'bg-surface-container-high text-on-surface';
  }
}

function getVehicleLabel(reservation?: ReservationDetail | null) {
  if (reservation?.vehicle?.make || reservation?.vehicle?.model) {
    return [reservation.vehicle?.make, reservation.vehicle?.model]
      .filter(Boolean)
      .join(' ');
  }

  return (
    reservation?.tripDetails?.vehicle_label ||
    reservation?.tripDetails?.vehicle_type ||
    'Imported reservation'
  );
}

function getCustomerLabel(reservation?: ReservationDetail | null) {
  const fullName =
    reservation?.customer?.name ||
    [reservation?.user?.firstName, reservation?.user?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  return (
    fullName ||
    reservation?.tripDetails?.customer_name ||
    reservation?.tripDetails?.passenger_name ||
    reservation?.customer?.email ||
    reservation?.user?.email ||
    reservation?.tripDetails?.customer_email ||
    'Unknown customer'
  );
}

function getAssignmentSummary(requirement: any, assignments: any[]) {
  const assigned = assignments.filter(
    (assignment) =>
      !requirement?.id || assignment.quoteRequirementId === requirement.id
  );
  const required = Number(requirement?.quantity || 1);
  return { assigned, required: Number.isFinite(required) && required > 0 ? required : 1 };
}

function getDriverAssignmentLabel(reservation?: ReservationDetail | null) {
  const assignments: any[] = Array.isArray(reservation?.driverAssignments)
    ? reservation?.driverAssignments || []
    : [];
  if (assignments.length) {
    return assignments
      .map((assignment: any) => assignment.driver?.name)
      .filter(Boolean)
      .join(', ');
  }
  return reservation?.tripDetails?.driver_name || 'No exact driver assigned';
}

function formatAddress(address: any, fallback?: string | null) {
  if (typeof address === 'string') return address;
  if (address?.formatted) return address.formatted;
  const parts = [
    address?.name,
    address?.line1,
    address?.line2,
    [address?.city, address?.state, address?.postal_code].filter(Boolean).join(', '),
  ].filter(Boolean);
  return parts.join(' · ') || fallback || 'N/A';
}

function DetailRow({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-on-surface-variant">{label}</dt>
      <dd
        className={`min-w-0 text-right text-on-surface ${
          mono ? 'break-all font-mono text-xs' : 'break-words'
        }`}
      >
        {children}
      </dd>
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  action,
  children,
  className = '',
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`card p-4 ${className}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-on-surface">{title}</h4>
          {subtitle ? (
            <p className="mt-1 text-sm text-on-surface-variant">{subtitle}</p>
          ) : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SummaryTile({
  label,
  value,
  detail,
  tone = 'default',
}: {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'info';
}) {
  const toneClass =
    tone === 'success'
      ? 'bg-success-container/25'
      : tone === 'warning'
      ? 'bg-warning-container/25'
      : tone === 'info'
      ? 'bg-info-container/25'
      : 'bg-surface-container-low';

  return (
    <div className={`min-w-0 rounded-card p-3 ${toneClass}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
        {label}
      </p>
      <div className="mt-1 min-w-0 break-words text-sm font-semibold text-on-surface">{value}</div>
      {detail ? (
        <p className="mt-1 min-w-0 break-words text-xs text-on-surface-variant">{detail}</p>
      ) : null}
    </div>
  );
}

function VehicleAssignmentCard({
  reservation,
  requirement,
  assignments,
  onChanged,
}: {
  reservation: ReservationDetail;
  requirement: any;
  assignments: any[];
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [notes, setNotes] = useState('');
  const { assigned, required } = getAssignmentSummary(requirement, assignments);
  const requirementId = requirement?.id || null;
  const canAssignMore = assigned.length < required;

  const availableVehiclesQuery = useQuery(
    ['reservation-available-vehicles', reservation.id, requirementId],
    async () => {
      const response = await adminAPI.getAvailableReservationVehicles(reservation.id, {
        requirementId: requirementId || undefined,
      });
      return Array.isArray(response.data?.candidates) ? response.data.candidates : [];
    },
    { enabled: Boolean(reservation.id) && canAssignMore, staleTime: 30_000 }
  );

  const assignMutation = useMutation(
    async () =>
      adminAPI.createReservationVehicleAssignment(reservation.id, {
        quoteRequirementId: requirementId,
        vehicleId: selectedVehicleId,
        notes: notes || null,
      }),
    {
      onSuccess: async () => {
        toast.success('Vehicle assigned');
        setSelectedVehicleId('');
        setNotes('');
        await queryClient.invalidateQueries(['reservation-available-vehicles', reservation.id, requirementId]);
        onChanged();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to assign vehicle');
      },
    }
  );

  const removeMutation = useMutation(
    async (assignmentId: string) =>
      adminAPI.deleteReservationVehicleAssignment(reservation.id, assignmentId),
    {
      onSuccess: async () => {
        toast.success('Vehicle assignment removed');
        await queryClient.invalidateQueries(['reservation-available-vehicles', reservation.id, requirementId]);
        onChanged();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to remove assignment');
      },
    }
  );

  const candidates = availableVehiclesQuery.data || [];
  const availableCandidates = candidates.filter((candidate: any) => candidate.available);

  return (
    <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-on-surface">
            {formatLabel(requirement?.vehicle_label || requirement?.vehicle_type || 'Vehicle')}
          </h4>
          <p className="mt-1 text-sm text-on-surface-variant">
            {assigned.length}/{required} assigned
            {requirement?.passenger_capacity_required ? ` · ${requirement.passenger_capacity_required}+ passengers` : ''}
          </p>
        </div>
        <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${
          assigned.length >= required
            ? 'bg-success-container text-success-on-container'
            : assigned.length > 0
            ? 'bg-info-container text-info-on-container'
            : 'bg-warning-container text-warning-on-container'
        }`}>
          {assigned.length >= required ? 'Assigned' : assigned.length > 0 ? 'Partial' : 'Unassigned'}
        </span>
      </div>

      <div className="mt-4 space-y-2">
        {assigned.length ? assigned.map((assignment) => (
          <div key={assignment.id} className="flex flex-wrap items-center justify-between gap-3 rounded-card bg-surface-container-low p-3">
            <div>
              <p className="text-sm font-medium text-on-surface">
                {[assignment.vehicle?.fleetNumber, assignment.vehicle?.make, assignment.vehicle?.model].filter(Boolean).join(' ') || 'Assigned vehicle'}
              </p>
              <p className="text-xs text-on-surface-variant">
                {[assignment.vehicle?.licensePlate, assignment.vehicle?.vehicleType, assignment.vehicle?.passengerCapacity ? `${assignment.vehicle.passengerCapacity} pax` : null].filter(Boolean).join(' · ')}
              </p>
            </div>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={removeMutation.isLoading}
              onClick={() => removeMutation.mutate(assignment.id)}
            >
              Remove
            </button>
          </div>
        )) : (
          <p className="rounded-card bg-surface-container-low p-3 text-sm text-on-surface-variant">
            No exact vehicle assigned yet.
          </p>
        )}
      </div>

      {canAssignMore ? (
        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
          <div>
            <label className="mb-1 block text-xs font-medium text-on-surface-variant">Available vehicle</label>
            <select
              className="input-field"
              value={selectedVehicleId}
              onChange={(event) => setSelectedVehicleId(event.target.value)}
              disabled={availableVehiclesQuery.isLoading}
            >
              <option value="">{availableVehiclesQuery.isLoading ? 'Loading vehicles...' : 'Select vehicle'}</option>
              {availableCandidates.map((vehicle: any) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {[vehicle.fleetNumber, vehicle.make, vehicle.model, vehicle.licensePlate].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
            {!availableVehiclesQuery.isLoading && !availableCandidates.length ? (
              <p className="mt-1 text-xs text-error">No available matching vehicles for this time window.</p>
            ) : null}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-on-surface-variant">Notes</label>
            <input
              className="input-field"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional assignment note"
            />
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={!selectedVehicleId || assignMutation.isLoading}
            onClick={() => assignMutation.mutate()}
          >
            {assignMutation.isLoading ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function DriverAssignmentCard({
  reservation,
  vehicleAssignment,
  assignments,
  onChanged,
}: {
  reservation: ReservationDetail;
  vehicleAssignment?: any | null;
  assignments: any[];
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [notes, setNotes] = useState('');
  const vehicleAssignmentId = vehicleAssignment?.id || null;
  const assigned = assignments.filter((assignment) =>
    vehicleAssignmentId
      ? assignment.vehicleAssignmentId === vehicleAssignmentId
      : !assignment.vehicleAssignmentId
  );
  const canAssignMore = assigned.length === 0;

  const candidatesQuery = useQuery(
    ['reservation-available-drivers', reservation.id, vehicleAssignmentId],
    async () => {
      const response = await adminAPI.getAvailableReservationDrivers(reservation.id, {
        vehicleAssignmentId,
      });
      return Array.isArray(response.data?.candidates) ? response.data.candidates : [];
    },
    { enabled: Boolean(reservation.id) && canAssignMore, staleTime: 30_000 }
  );

  const assignMutation = useMutation(
    async () =>
      adminAPI.createReservationDriverAssignment(reservation.id, {
        driverId: selectedDriverId,
        vehicleAssignmentId,
        notes: notes || null,
      }),
    {
      onSuccess: async () => {
        toast.success('Driver assigned');
        setSelectedDriverId('');
        setNotes('');
        await queryClient.invalidateQueries(['reservation-available-drivers', reservation.id, vehicleAssignmentId]);
        onChanged();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to assign driver');
      },
    }
  );

  const removeMutation = useMutation(
    async (assignmentId: string) =>
      adminAPI.deleteReservationDriverAssignment(reservation.id, assignmentId),
    {
      onSuccess: async () => {
        toast.success('Driver assignment removed');
        await queryClient.invalidateQueries(['reservation-available-drivers', reservation.id, vehicleAssignmentId]);
        onChanged();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to remove driver assignment');
      },
    }
  );

  const candidates = candidatesQuery.data || [];
  const availableCandidates = candidates.filter((candidate: any) => candidate.available);
  const label = vehicleAssignment
    ? [vehicleAssignment.vehicle?.fleetNumber, vehicleAssignment.vehicle?.make, vehicleAssignment.vehicle?.model]
        .filter(Boolean)
        .join(' ') || 'Assigned vehicle'
    : 'Reservation driver';

  return (
    <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-on-surface">{label}</h4>
          <p className="mt-1 text-sm text-on-surface-variant">
            {assigned.length ? 'Driver assigned' : 'Needs driver'}
            {vehicleAssignment?.vehicle?.vehicleType ? ` · ${formatLabel(vehicleAssignment.vehicle.vehicleType)}` : ''}
          </p>
        </div>
        <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${
          assigned.length
            ? 'bg-success-container text-success-on-container'
            : 'bg-warning-container text-warning-on-container'
        }`}>
          {assigned.length ? 'Assigned' : 'Unassigned'}
        </span>
      </div>

      <div className="mt-4 space-y-2">
        {assigned.length ? assigned.map((assignment) => (
          <div key={assignment.id} className="flex flex-wrap items-center justify-between gap-3 rounded-card bg-surface-container-low p-3">
            <div>
              <p className="text-sm font-medium text-on-surface">
                {assignment.driver?.name || 'Assigned driver'}
              </p>
              <p className="text-xs text-on-surface-variant">
                {[assignment.driver?.phone, assignment.driver?.email, formatLabel(assignment.driver?.employmentType)].filter(Boolean).join(' · ')}
              </p>
              {assignment.notes ? (
                <p className="mt-1 text-xs text-on-surface-variant">{assignment.notes}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={removeMutation.isLoading}
              onClick={() => removeMutation.mutate(assignment.id)}
            >
              Remove
            </button>
          </div>
        )) : (
          <p className="rounded-card bg-surface-container-low p-3 text-sm text-on-surface-variant">
            No exact driver assigned yet.
          </p>
        )}
      </div>

      {canAssignMore ? (
        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
          <div>
            <label className="mb-1 block text-xs font-medium text-on-surface-variant">Eligible driver</label>
            <select
              className="input-field"
              value={selectedDriverId}
              onChange={(event) => setSelectedDriverId(event.target.value)}
              disabled={candidatesQuery.isLoading}
            >
              <option value="">{candidatesQuery.isLoading ? 'Loading drivers...' : 'Select driver'}</option>
              {candidates.map((driver: any) => (
                <option key={driver.id} value={driver.id} disabled={!driver.available}>
                  {driver.name} {driver.available ? '' : `- ${driver.blockingIssues?.join('; ') || 'Unavailable'}`}
                </option>
              ))}
            </select>
            {!candidatesQuery.isLoading && !availableCandidates.length ? (
              <p className="mt-1 text-xs text-error">No eligible drivers for this reservation window.</p>
            ) : null}
            {selectedDriverId ? (
              <CandidateWarnings candidate={candidates.find((driver: any) => driver.id === selectedDriverId)} />
            ) : null}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-on-surface-variant">Notes</label>
            <input
              className="input-field"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional assignment note"
            />
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={!selectedDriverId || assignMutation.isLoading}
            onClick={() => assignMutation.mutate()}
          >
            {assignMutation.isLoading ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function CandidateWarnings({ candidate }: { candidate?: any }) {
  if (!candidate) {
    return null;
  }
  const warnings = [
    ...(candidate.blockingIssues || []),
    ...(candidate.warnings || []),
  ];
  if (!warnings.length) {
    return null;
  }
  return (
    <div className="mt-2 rounded-card bg-surface-container-low p-2 text-xs text-on-surface-variant">
      {warnings.join(' · ')}
    </div>
  );
}

export default function ReservationDetailDrawer({
  open,
  onClose,
  reservationId,
  onReservationUpdated,
}: ReservationDetailDrawerProps) {
  const queryClient = useQueryClient();
  const [drawerTab, setDrawerTab] = useState<ReservationDrawerTab>('overview');
  const [selectedAccount, setSelectedAccount] =
    useState<AccountTypeaheadOption | null>(null);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerTypeaheadOption | null>(null);
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceSearchOption | null>(null);
  const [invoicePublicLinks, setInvoicePublicLinks] = useState<Record<string, string>>({});
  const [groupedPaymentLinks, setGroupedPaymentLinks] = useState<Record<string, string>>({});
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [selectedFullInvoice, setSelectedFullInvoice] = useState<Partial<Invoice> | null>(null);
  const [manualPaymentTarget, setManualPaymentTarget] = useState<ManualPaymentTarget | null>(null);
  const [manualPaymentForm, setManualPaymentForm] = useState<ManualPaymentDetails>({
    paymentMethod: 'check',
    note: '',
  });

  useEffect(() => {
    if (open) {
      setDrawerTab('overview');
      setSelectedAccount(null);
      setSelectedCustomer(null);
      setSelectedInvoice(null);
      setInvoicePublicLinks({});
      setGroupedPaymentLinks({});
      setShowCreateInvoice(false);
      setSelectedFullInvoice(null);
      setManualPaymentTarget(null);
      setManualPaymentForm({ paymentMethod: 'check', note: '' });
    }
  }, [open, reservationId]);

  const {
    data: reservation,
    isLoading,
    error,
  } = useQuery<ReservationDetail | null>(
    ['admin-reservation', reservationId],
    async () => {
      if (!reservationId) {
        return null;
      }

      const response = await adminAPI.getReservation(reservationId);
      return response.data;
    },
    {
      enabled: open && Boolean(reservationId),
      staleTime: 60_000,
      retry: false,
    }
  );

  const refreshReservationQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries('admin-reservations'),
      reservationId
        ? queryClient.invalidateQueries(['admin-reservation', reservationId])
        : Promise.resolve(),
    ]);
    onReservationUpdated?.();
  };

  const accountMutation = useMutation(
    async (accountId: string | null) => {
      if (!reservationId) {
        throw new Error('Reservation not selected');
      }

      return adminAPI.updateReservation(reservationId, {
        accountId,
        customerCompanyName: accountId
          ? undefined
          : reservation?.tripDetails?.customer_company_name || null,
        changeReason: 'Updated reservation account from detail drawer',
      });
    },
    {
      onSuccess: async () => {
        toast.success('Reservation account updated');
        setSelectedAccount(null);
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            'Failed to update reservation account'
        );
      },
    }
  );

  const customerMutation = useMutation(
    async (payload: {
      customerReference: string | null;
      customerEmail: string | null;
      customerName: string | null;
    }) => {
      if (!reservationId) {
        throw new Error('Reservation not selected');
      }

      return adminAPI.updateReservation(reservationId, {
        ...payload,
        changeReason: 'Updated reservation customer snapshot from detail drawer',
      });
    },
    {
      onSuccess: async () => {
        toast.success('Reservation customer snapshot updated');
        setSelectedCustomer(null);
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            'Failed to update reservation customer snapshot'
        );
      },
    }
  );

  const attachInvoiceMutation = useMutation(
    async (invoiceId: string) => {
      if (!reservationId) {
        throw new Error('Reservation not selected');
      }

      return adminAPI.attachReservationInvoice(reservationId, invoiceId);
    },
    {
      onSuccess: async () => {
        toast.success('Invoice attached to reservation');
        setSelectedInvoice(null);
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to attach invoice'
        );
      },
    }
  );

  const detachInvoiceMutation = useMutation(
    async (invoiceId: string) => {
      if (!reservationId) {
        throw new Error('Reservation not selected');
      }

      return adminAPI.detachReservationInvoice(reservationId, invoiceId);
    },
    {
      onSuccess: async () => {
        toast.success('Invoice detached from reservation');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to detach invoice'
        );
      },
    }
  );

  const publicRecordLinkMutation = useMutation(
    async () => {
      if (!reservationId) {
        throw new Error('Reservation not selected');
      }
      return adminAPI.generateReservationPublicRecordLink(reservationId, {
        baseUrl: window.location.origin,
      });
    },
    {
      onSuccess: async (response) => {
        const publicUrl = response.data?.public_url;
        if (publicUrl) {
          await navigator.clipboard?.writeText(publicUrl).catch(() => undefined);
          toast.success('Customer record link copied');
        } else {
          toast.success('Customer record link generated');
        }
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            'Failed to generate customer record link'
        );
      },
    }
  );

  const createBalanceInvoiceMutation = useMutation(
    async () => {
      if (!reservationId) {
        throw new Error('Reservation not selected');
      }

      return adminAPI.createReservationBalanceInvoice(reservationId);
    },
    {
      onSuccess: async () => {
        toast.success('Balance invoice created');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to create balance invoice'
        );
      },
    }
  );

  const createDepositInvoiceMutation = useMutation(
    async () => {
      if (!reservationId) {
        throw new Error('Reservation not selected');
      }

      return adminAPI.createReservationDepositInvoice(reservationId, {
        baseUrl: window.location.origin,
      });
    },
    {
      onSuccess: async (response) => {
        const provisioningError = response.data?.depositProvisioning?.error;
        if (provisioningError) {
          toast.error(provisioningError);
        } else {
          toast.success('Deposit invoice and payment request created');
        }
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to create deposit invoice'
        );
      },
    }
  );

  const finalizeBalanceInvoiceMutation = useMutation(
    async () => {
      if (!balanceInvoice?.id) {
        throw new Error('Balance invoice not selected');
      }

      return adminAPI.finalizeInvoice(balanceInvoice.id);
    },
    {
      onSuccess: async () => {
        toast.success('Balance invoice finalized');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to finalize balance invoice'
        );
      },
    }
  );

  const generateInvoiceLinkMutation = useMutation(
    async () => {
      if (!balanceInvoice?.id) {
        throw new Error('Balance invoice not selected');
      }

      const response = await adminAPI.getInvoicePublicLink(balanceInvoice.id);
      const publicUrl = getPublicUrlFromResponse(response);
      if (!publicUrl) {
        throw new Error('Invoice link was not returned');
      }

      return { invoiceId: balanceInvoice.id, publicUrl };
    },
    {
      onSuccess: async ({ invoiceId, publicUrl }) => {
        setInvoicePublicLinks((current) => ({ ...current, [invoiceId]: publicUrl }));
        await copyTextToClipboard(publicUrl, 'Invoice payment link copied');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to generate invoice link'
        );
      },
    }
  );

  const sendInvoiceMutation = useMutation(
    async () => {
      if (!balanceInvoice?.id) {
        throw new Error('Balance invoice not selected');
      }

      return adminAPI.sendInvoice(balanceInvoice.id);
    },
    {
      onSuccess: async () => {
        toast.success('Balance invoice email sent');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to send balance invoice'
        );
      },
    }
  );

  const markBalanceInvoicePaidMutation = useMutation(
    async (paymentDetails: ManualPaymentDetails) => {
      if (!balanceInvoice?.id) {
        throw new Error('Balance invoice not selected');
      }

      return adminAPI.markInvoicePaid(balanceInvoice.id, paymentDetails);
    },
    {
      onSuccess: async () => {
        setManualPaymentTarget(null);
        setManualPaymentForm({ paymentMethod: 'check', note: '' });
        toast.success('Balance invoice marked paid');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to mark invoice paid'
        );
      },
    }
  );

  const createGroupedPaymentRequestMutation = useMutation(
    async () => {
      if (!balanceInvoice?.id) {
        throw new Error('Balance invoice not selected');
      }

      const response = await paymentRequestAPI.create({
        invoiceIds: [balanceInvoice.id],
        baseUrl: window.location.origin,
      });
      const publicUrl = getPublicUrlFromResponse(response);
      const requestId = response?.data?.request?.id;

      return { requestId, publicUrl };
    },
    {
      onSuccess: async ({ requestId, publicUrl }) => {
        if (requestId && publicUrl) {
          setGroupedPaymentLinks((current) => ({
            ...current,
            [requestId]: publicUrl,
          }));
          await copyTextToClipboard(publicUrl, 'Grouped payment request copied');
        } else {
          toast.success('Grouped payment request created');
        }
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            'Failed to create grouped payment request'
        );
      },
    }
  );

  const generateGroupedPaymentLinkMutation = useMutation(
    async () => {
      const requestId = billingSummary.balancePaymentRequest?.id;
      if (!requestId) {
        throw new Error('Grouped payment request not selected');
      }

      const response = await paymentRequestAPI.generateLink(requestId, {
        baseUrl: window.location.origin,
      });
      const publicUrl = getPublicUrlFromResponse(response);
      if (!publicUrl) {
        throw new Error('Grouped payment link was not returned');
      }

      return { requestId, publicUrl };
    },
    {
      onSuccess: async ({ requestId, publicUrl }) => {
        setGroupedPaymentLinks((current) => ({ ...current, [requestId]: publicUrl }));
        await copyTextToClipboard(publicUrl, 'Grouped payment link copied');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to generate grouped payment link'
        );
      },
    }
  );

  const sendGroupedPaymentLinkMutation = useMutation(
    async () => {
      const requestId = billingSummary.balancePaymentRequest?.id;
      if (!requestId) {
        throw new Error('Grouped payment request not selected');
      }

      return paymentRequestAPI.sendLink(requestId, {
        baseUrl: window.location.origin,
      });
    },
    {
      onSuccess: async (response) => {
        const publicUrl = getPublicUrlFromResponse(response);
        const requestId = billingSummary.balancePaymentRequest?.id;
        if (requestId && publicUrl) {
          setGroupedPaymentLinks((current) => ({
            ...current,
            [requestId]: publicUrl,
          }));
        }
        toast.success('Grouped payment email sent');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to send grouped payment email'
        );
      },
    }
  );

  const markGroupedPaymentPaidMutation = useMutation(
    async (paymentDetails: ManualPaymentDetails) => {
      const requestId = billingSummary.balancePaymentRequest?.id;
      if (!requestId) {
        throw new Error('Grouped payment request not selected');
      }

      return paymentRequestAPI.markPaid(requestId, paymentDetails);
    },
    {
      onSuccess: async () => {
        setManualPaymentTarget(null);
        setManualPaymentForm({ paymentMethod: 'check', note: '' });
        toast.success('Grouped payment request marked paid');
        await refreshReservationQueries();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            'Failed to mark grouped payment request paid'
        );
      },
    }
  );

  const relatedInvoices = useMemo<ReservationInvoice[]>(() => {
    const invoices = reservation?.relatedInvoices;
    return Array.isArray(invoices) ? invoices : [];
  }, [reservation?.relatedInvoices]);
  const relatedPayments: ReservationInvoice[] = useMemo(() => {
    const payments = reservation?.relatedPayments;
    if (Array.isArray(payments)) {
      return payments;
    }

    return relatedInvoices.filter((invoice) => {
      const status = String(invoice?.status || '').toLowerCase();
      return status === 'paid' || Number(invoice?.amount_paid || 0) > 0;
    });
  }, [reservation?.relatedPayments, relatedInvoices]);
  const quoteDepositInvoice = useMemo(() => {
    const quoteNumber =
      reservation?.tripDetails?.quote_number ||
      reservation?.splitBillingConfig?.quote_number ||
      reservation?.reservationRef ||
      '';
    return relatedInvoices.find((invoice) => {
      const haystack = [
        invoice.invoice_number,
        invoice.description,
        invoice.memo,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return (
        haystack.includes('deposit') ||
        (!!quoteNumber && haystack.includes(String(quoteNumber).toLowerCase()))
      );
    });
  }, [
    relatedInvoices,
    reservation?.reservationRef,
    reservation?.splitBillingConfig?.quote_number,
    reservation?.tripDetails?.quote_number,
  ]);
  const quoteDepositPaid = quoteDepositInvoice
    ? String(quoteDepositInvoice.status || '').toLowerCase() === 'paid' ||
      Number(quoteDepositInvoice.amount_paid || 0) > 0
    : false;
  const billingSummary = reservation?.billingSummary || {};
  const billingInvoices = Array.isArray(billingSummary.invoices)
    ? billingSummary.invoices
    : [];
  const balanceInvoice =
    billingSummary.balanceInvoice ||
    billingInvoices.find((invoice) => invoice.kind === 'balance') ||
    null;
  const allowedBillingActions = billingSummary.allowedActions || {};
  const balancePaymentRequest = billingSummary.balancePaymentRequest || null;
  const balanceInvoiceLink =
    (balanceInvoice?.id ? invoicePublicLinks[balanceInvoice.id] : null) ||
    balanceInvoice?.paymentUrl ||
    null;
  const groupedPaymentLink =
    (balancePaymentRequest?.id ? groupedPaymentLinks[balancePaymentRequest.id] : null) ||
    balancePaymentRequest?.publicUrl ||
    (balancePaymentRequest ? billingSummary.balancePaymentUrl : null) ||
    null;
  const manualPaymentSaving =
    markBalanceInvoicePaidMutation.isLoading ||
    markGroupedPaymentPaidMutation.isLoading;
  const openManualPaymentPanel = (target: ManualPaymentTarget) => {
    setManualPaymentTarget(target);
    setManualPaymentForm({ paymentMethod: 'check', note: '' });
  };
  const submitManualPayment = () => {
    const paymentMethod = manualPaymentForm.paymentMethod.trim();
    if (!paymentMethod) {
      toast.error('Payment method is required');
      return;
    }
    const payload = {
      paymentMethod,
      ...(manualPaymentForm.note?.trim()
        ? { note: manualPaymentForm.note.trim() }
        : {}),
    };
    if (manualPaymentTarget === 'balance_invoice') {
      markBalanceInvoicePaidMutation.mutate(payload);
      return;
    }
    if (manualPaymentTarget === 'grouped_request') {
      markGroupedPaymentPaidMutation.mutate(payload);
    }
  };
  const depositPaymentUrl =
    billingSummary.depositPaymentUrl ||
    reservation?.quoteSource?.depositPaymentUrl ||
    null;
  const canCreateBalanceInvoice =
    Boolean(allowedBillingActions.createBalanceInvoice);
  const canCreateDepositInvoice =
    Boolean(allowedBillingActions.createDepositInvoice);

  const loadInvoiceOptions = async (
    query: string
  ): Promise<InvoiceSearchOption[]> => {
    const response = await adminAPI.getAllInvoices({
      search: query || undefined,
      page: 1,
      limit: 10,
    });

    const invoices = Array.isArray(response?.data?.invoices)
      ? response.data.invoices
      : [];
    const attachedInvoiceIds = new Set(
      relatedInvoices.map((invoice) => invoice.id)
    );

    return invoices
      .filter(
        (invoice: any) =>
          invoice?.id && !attachedInvoiceIds.has(String(invoice.id))
      )
      .map(
        (invoice: any) =>
          ({
            id: String(invoice.id),
            invoiceId: String(invoice.id),
            label: invoice.invoice_number || `Invoice ${invoice.id}`,
            description:
              invoice.customer_details?.email || invoice.description || '',
          } satisfies InvoiceSearchOption)
      );
  };

  const drawerTitle =
    reservation?.reservationRef ||
    (reservationId
      ? `Reservation ${reservationId.slice(0, 8)}`
      : 'Reservation');
  const drawerSubtitle = reservationId
    ? `Reservation ID: ${reservationId}`
    : undefined;
  const accountName =
    reservation?.account?.name ||
    reservation?.tripDetails?.customer_company_name ||
    'No attached account';
  const customerSnapshotId =
    reservation?.customerReference || reservation?.customer?.id || 'N/A';
  const vehicleAssignmentCount = reservation?.vehicleAssignments?.length || 0;
  const driverAssignmentCount = reservation?.driverAssignments?.length || 0;
  const driverRequiredCount = reservation?.driverReadiness?.required || 0;
  const quoteNumber =
    reservation?.tripDetails?.quote_number ||
    reservation?.splitBillingConfig?.quote_number ||
    reservation?.reservationRef ||
    'N/A';

  const initialInvoiceReservation = useMemo(() => {
    if (!reservation?.id) return null;
    return {
      id: reservation.id,
      label: reservation.reservationRef || `Reservation ${reservation.id.slice(0, 8)}`,
      description: getCustomerLabel(reservation),
      reservationRef: reservation.reservationRef,
      startDate: reservation.startDate,
      endDate: reservation.endDate,
      customerName: getCustomerLabel(reservation),
    };
  }, [reservation]);

  const initialInvoiceAccount = useMemo<AccountTypeaheadOption | null>(() => {
    if (!reservation?.account?.id) return null;
    return {
      id: reservation.account.id,
      label: reservation.account.name || 'Reservation account',
      description: 'Linked reservation account',
    };
  }, [reservation?.account?.id, reservation?.account?.name]);

  const initialInvoiceCustomer = useMemo<CustomerTypeaheadOption | null>(() => {
    const candidateId = reservation?.customer?.id || reservation?.customerReference || '';
    const isValidCustomerId =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidateId) ||
      /^cus_[A-Za-z0-9]+$/.test(candidateId);
    if (!isValidCustomerId) return null;
    const email = reservation?.customer?.email || reservation?.tripDetails?.customer_email || '';
    return {
      id: candidateId,
      label: getCustomerLabel(reservation),
      description: email || 'Reservation customer',
      email,
    };
  }, [reservation]);
  const depositLabel = quoteDepositPaid
    || billingSummary.depositStatus === 'paid'
    ? 'Deposit paid'
    : reservation?.paymentStatus === 'deposit_due' || billingSummary.depositStatus === 'due'
    ? 'Deposit due'
    : 'No deposit required';
  const drawerTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'itinerary', label: 'Itinerary' },
	    {
	      key: 'vehicles',
	      label: 'Vehicles',
	      count: vehicleAssignmentCount,
	    },
	    {
	      key: 'drivers',
	      label: 'Drivers',
	      count: driverAssignmentCount,
	    },
	    { key: 'billing', label: 'Billing' },
    { key: 'invoices', label: 'Invoices', count: relatedInvoices.length },
    { key: 'payments', label: 'Payments', count: relatedPayments.length },
  ];

  return (
    <EntityDrawer
      open={open}
      onClose={onClose}
      title={drawerTitle}
      subtitle={drawerSubtitle}
      widthClassName="max-w-4xl"
      headerActions={
        reservation?.lifecycleLabel || reservation?.status ? (
          <>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={publicRecordLinkMutation.isLoading}
              onClick={() => publicRecordLinkMutation.mutate()}
            >
              {publicRecordLinkMutation.isLoading
                ? 'Generating...'
                : 'Copy Customer Record'}
            </button>
            <span
              className={`rounded-pill whitespace-nowrap px-2 py-1 text-xs font-semibold ${getStatusClass(
                reservation.lifecycleLabel || reservation.status
              )}`}
            >
              {reservation.lifecycleLabel || formatLabel(reservation.status)}
            </span>
          </>
        ) : null
      }
    >
      {!reservationId ? null : isLoading ? (
        <LoadingSpinner className="py-20" />
      ) : error ? (
        <div className="rounded-card border border-error/40 bg-error-container/20 p-4 text-sm text-on-surface">
          Failed to load reservation details.{' '}
          {(error as any)?.response?.data?.message ||
            (error as any)?.message ||
            'Unknown error'}
        </div>
      ) : reservation ? (
        <div className="space-y-5">
          <FarmOutReservationPanel reservationId={reservation.id} />
          <div className="sticky top-0 z-10 -mx-6 border-b border-outline/60 bg-surface-container-lowest px-6 pb-3 pt-1">
            <div className="overflow-x-auto">
              <Tabs
                tabs={drawerTabs}
                activeTab={drawerTab}
                onChange={(tab) => setDrawerTab(tab as ReservationDrawerTab)}
                className="min-w-max"
              />
            </div>
          </div>

          {drawerTab === 'overview' ? (
            <>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <SectionCard title="Trip" subtitle={formatDateTime(reservation.startDate)}>
                  <dl className="space-y-2 text-sm">
                    <DetailRow label="Customer">{getCustomerLabel(reservation)}</DetailRow>
                    <DetailRow label="Account">{accountName}</DetailRow>
                    <DetailRow label="Ends">{formatDateTime(reservation.endDate)}</DetailRow>
                    <DetailRow label="Source">{formatLabel(reservation.sourceType || 'booking')}</DetailRow>
                  </dl>
                </SectionCard>
                <SectionCard title="Billing" subtitle={depositLabel}>
                  <dl className="space-y-2 text-sm">
                    <DetailRow label="Contract total">{formatDollars(billingSummary.contractTotal || reservation.totalPrice)}</DetailRow>
                    <DetailRow label="Deposit">{formatDollars(billingSummary.depositRequired || reservation.depositAmount)}</DetailRow>
                    <DetailRow label="Balance">{formatDollars(billingSummary.balanceRemaining || 0)}</DetailRow>
                    <DetailRow label="Invoices">{relatedInvoices.length}</DetailRow>
                  </dl>
                </SectionCard>
                <SectionCard title="Assignment" subtitle={formatLabel(reservation.assignmentStatus || 'unassigned')}>
                  <dl className="space-y-2 text-sm">
                    <DetailRow label="Vehicles">{vehicleAssignmentCount} exact assigned</DetailRow>
                    <DetailRow label="Drivers">{driverAssignmentCount}/{driverRequiredCount || 0} exact assigned</DetailRow>
                    <DetailRow label="Legacy vehicle">{getVehicleLabel(reservation)}</DetailRow>
                    <DetailRow label="Legacy driver">{getDriverAssignmentLabel(reservation)}</DetailRow>
                  </dl>
                </SectionCard>
                <SectionCard title="Status" subtitle={reservation.lifecycleLabel || formatLabel(reservation.status)}>
                  <dl className="space-y-2 text-sm">
                    <DetailRow label="Lifecycle">
                      <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${getStatusClass(reservation.lifecycleLabel || reservation.status)}`}>
                        {reservation.lifecycleLabel || formatLabel(reservation.status)}
                      </span>
                    </DetailRow>
                    <DetailRow label="Dispatch">{formatLabel(reservation.dispatchStatus || 'not_ready')}</DetailRow>
                    <DetailRow label="Customer ref" mono>{customerSnapshotId}</DetailRow>
                  </dl>
                </SectionCard>
              </div>

              {reservation.readiness?.groups?.length ? (
                <SectionCard
                  title="Operational Readiness"
                  subtitle={`${reservation.readiness.blockingIssues?.length || 0} blockers · ${reservation.readiness.warnings?.length || 0} warnings`}
                  action={
                    <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${getStatusClass(reservation.dispatchStatus || reservation.lifecycleLabel || reservation.status)}`}>
                      {formatLabel(reservation.dispatchStatus || 'not_ready')}
                    </span>
                  }
                >
                  <div className="mt-4 overflow-hidden rounded-card border border-outline/50">
                    {reservation.readiness.groups.map((group) => (
                      <div
                        key={group.key}
                        className="border-b border-outline/50 bg-surface-container-low px-3 py-3 last:border-b-0"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-on-surface">{group.label}</p>
                          <span className={`rounded-pill px-2 py-0.5 text-xs font-semibold ${getStatusClass(group.state)}`}>
                            {formatLabel(group.state)}
                          </span>
                        </div>
                        {[...(group.blockingIssues || []), ...(group.warnings || [])].length ? (
                          <ul className="mt-2 space-y-1 text-xs text-on-surface-variant">
                            {[...(group.blockingIssues || []), ...(group.warnings || [])]
                              .slice(0, 3)
                              .map((message) => (
                                <li key={message} className="flex gap-2">
                                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
                                  <span>{message}</span>
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p className="mt-2 text-xs text-on-surface-variant">Ready for this stage.</p>
                        )}
                      </div>
                    ))}
                  </div>
                </SectionCard>
              ) : null}

              <RecordContactSnapshotsPanel
                snapshots={reservation.contactSnapshots}
                subtitle="Trip, billing, passenger, and matched customer contacts saved with this reservation."
              />

              {String(reservation.sourceType || '').toLowerCase() === 'quote' ||
              reservation.tripDetails?.quote_number ? (
                <SectionCard
                  title="Quote Source"
                  subtitle={quoteNumber}
                  action={
                    <span className="rounded-pill bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700">
                      Quote converted
                    </span>
                  }
                >
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-card bg-surface-container-low p-3 text-sm">
                      <p className="text-xs font-semibold uppercase text-on-surface-variant">
                        Deposit
                      </p>
                      <p className="mt-1 font-semibold text-on-surface">
                        {formatDollars(reservation.depositAmount)}
                      </p>
                    </div>
                    <div className="rounded-card bg-surface-container-low p-3 text-sm">
                      <p className="text-xs font-semibold uppercase text-on-surface-variant">
                        Payment status
                      </p>
                      <span
                        className={`mt-2 inline-flex rounded-pill px-2 py-1 text-xs font-semibold ${getStatusClass(
                          quoteDepositPaid ? 'confirmed' : 'pending'
                        )}`}
                      >
                        {quoteDepositPaid
                          ? 'Deposit paid'
                          : quoteDepositInvoice
                          ? 'Deposit due'
                          : 'No deposit invoice'}
                      </span>
                    </div>
                    <div className="rounded-card bg-surface-container-low p-3 text-sm">
                      <p className="text-xs font-semibold uppercase text-on-surface-variant">
                        Deposit invoice
                      </p>
                      <p className="mt-1 font-semibold text-on-surface">
                        {quoteDepositInvoice?.invoice_number || 'Not created'}
                      </p>
                    </div>
                  </div>
                  {Array.isArray(
                    reservation.splitBillingConfig?.vehicle_requirements
                  ) ? (
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      {(
                        reservation.splitBillingConfig?.vehicle_requirements ||
                        []
                      ).map((requirement: any) => (
                        <div
                          key={
                            requirement.id ||
                            `${requirement.vehicle_type}-${requirement.quantity}`
                          }
                          className="rounded-card bg-surface-container-low p-3 text-sm"
                        >
                          <p className="font-medium text-on-surface">
                            {requirement.quantity}{' '}
                            {formatLabel(
                              requirement.vehicle_label ||
                                requirement.vehicle_type
                            )}
                          </p>
                          <p className="mt-1 text-on-surface-variant">
                            {formatLabel(
                              requirement.availability_status || 'unchecked'
                            )}{' '}
                            · {requirement.available_count ?? 0} available ·{' '}
                            {formatDollars(requirement.subtotal)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {reservation.splitBillingConfig?.terms_snapshot ? (
                    <div className="mt-4">
                      <QuoteTermsSummary
                        compact
                        terms={reservation.splitBillingConfig.terms_snapshot}
                      />
                    </div>
                  ) : null}
                </SectionCard>
              ) : null}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <SectionCard
                  title="Customer and Account"
                  subtitle="Customer contact and account context for this reservation."
                >
                  <dl className="mt-3 space-y-2 text-sm">
                    <DetailRow label="Customer">
                      {getCustomerLabel(reservation)}
                    </DetailRow>
                    <DetailRow label="Customer Reference" mono>
                      {customerSnapshotId}
                    </DetailRow>
                    <DetailRow label="Email" mono>
                        {reservation.customer?.email ||
                          reservation.tripDetails?.customer_email ||
                          'N/A'}
                    </DetailRow>
                    <DetailRow label="Account">{accountName}</DetailRow>
                  </dl>
                  <div className="mt-4 border-t border-outline/60 pt-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                      Link Customer Record
                    </p>
                    <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                      <CustomerTypeahead
                        value={selectedCustomer}
                        onChange={setSelectedCustomer}
                        placeholder="Search customer by name or email..."
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={
                          !selectedCustomer || customerMutation.isLoading
                        }
                        onClick={() => {
                          if (!selectedCustomer) {
                            return;
                          }

                          customerMutation.mutate({
                            customerReference: selectedCustomer.id || null,
                            customerEmail: selectedCustomer.email || null,
                            customerName: selectedCustomer.label || null,
                          });
                        }}
                      >
                        {customerMutation.isLoading
                          ? 'Saving...'
                          : 'Save Customer Link'}
                      </button>
                    </div>
                    {reservation?.customerReference ||
                    reservation?.customer?.email ||
                    reservation?.tripDetails?.customer_email ? (
                      <button
                        type="button"
                        className="btn-secondary mt-3"
                        disabled={customerMutation.isLoading}
                        onClick={() =>
                          customerMutation.mutate({
                            customerReference: null,
                            customerEmail: null,
                            customerName: null,
                          })
                        }
                      >
                        Remove Customer Link
                      </button>
                    ) : null}
                  </div>
                </SectionCard>

                <SectionCard
                  title="Trip Snapshot"
                  subtitle="Current operational route and assigned role summary."
                >
                  <dl className="mt-3 space-y-2 text-sm">
                    <DetailRow label="Vehicle">
                      {getVehicleLabel(reservation)}
                    </DetailRow>
                    <DetailRow label="Pickup">
                      {reservation.tripDetails?.pickup_location || 'N/A'}
                    </DetailRow>
                    <DetailRow label="Dropoff">
                      {reservation.tripDetails?.dropoff_location || 'N/A'}
                    </DetailRow>
                    <DetailRow label="Driver">
                      {getDriverAssignmentLabel(reservation)}
                    </DetailRow>
                  </dl>
                  <div className="mt-4 border-t border-outline/60 pt-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
                      Attach Account
                    </p>
                    <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                      <AccountTypeahead
                        value={selectedAccount}
                        onChange={setSelectedAccount}
                        placeholder="Search account by name..."
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={!selectedAccount || accountMutation.isLoading}
                        onClick={() => {
                          if (selectedAccount) {
                            accountMutation.mutate(selectedAccount.id);
                          }
                        }}
                      >
                        {accountMutation.isLoading
                          ? 'Saving...'
                          : 'Save Account'}
                      </button>
                    </div>
                    {reservation?.account?.id ? (
                      <button
                        type="button"
                        className="btn-secondary mt-3"
                        disabled={accountMutation.isLoading}
                        onClick={() => accountMutation.mutate(null)}
                      >
                        Remove Account
                      </button>
                    ) : null}
                  </div>
                </SectionCard>

                <SectionCard title="Reservation Timing">
                  <dl className="mt-3 space-y-2 text-sm">
                    <DetailRow label="Start">
                      {formatDateTime(reservation.startDate)}
                    </DetailRow>
                    <DetailRow label="End">
                      {formatDateTime(reservation.endDate)}
                    </DetailRow>
                    <DetailRow label="Created">
                      {formatDateTime(reservation.createdAt)}
                    </DetailRow>
                    <DetailRow label="Deposit">
                      {formatDollars(reservation.depositAmount)}
                    </DetailRow>
                  </dl>
                </SectionCard>

                <SectionCard title="Imported Fields">
                  <dl className="mt-3 space-y-2 text-sm">
                    <DetailRow label="Passenger">
                      {reservation.tripDetails?.passenger_name || 'N/A'}
                    </DetailRow>
                    <DetailRow label="Pickup Time">
                      {reservation.tripDetails?.pickup_time || 'N/A'}
                    </DetailRow>
                    <DetailRow label="Affiliate">
                      {reservation.tripDetails?.affiliate || 'N/A'}
                    </DetailRow>
                    <DetailRow label="Payment Method">
                      {reservation.tripDetails?.payment_method || 'N/A'}
                    </DetailRow>
                    <DetailRow label="Imported Rate">
                      {reservation.tripDetails?.imported_rate
                        ? formatDollars(reservation.tripDetails.imported_rate)
                        : 'N/A'}
                    </DetailRow>
                    <DetailRow label="Imported Cost">
                      {reservation.tripDetails?.imported_cost
                        ? formatDollars(reservation.tripDetails.imported_cost)
                        : 'N/A'}
                    </DetailRow>
                  </dl>
                </SectionCard>
              </div>
            </>
          ) : null}

          {drawerTab === 'itinerary' ? (
            <div className="space-y-4">
              <SectionCard
                title="Pickup, Stops, and Dropoff"
                subtitle="Routing snapshot used for operations."
              >
                <div className="mt-4 space-y-3">
                  <div className="rounded-card bg-surface-container-low p-3">
                    <p className="text-xs font-semibold uppercase text-on-surface-variant">Pickup</p>
                    <p className="mt-1 text-sm font-medium text-on-surface">
                      {formatAddress(reservation.tripDetails?.pickup_address, reservation.tripDetails?.pickup_location)}
                    </p>
                    {reservation.tripDetails?.pickup_address?.notes ? (
                      <p className="mt-1 text-sm text-on-surface-variant">{reservation.tripDetails.pickup_address.notes}</p>
                    ) : null}
                  </div>
                  {Array.isArray(reservation.tripDetails?.stops)
                    ? reservation.tripDetails?.stops.map((stop: any, index: number) => (
                        <div key={stop.id || index} className="rounded-card bg-surface-container-low p-3">
                          <p className="text-xs font-semibold uppercase text-on-surface-variant">Stop {index + 1}</p>
                          <p className="mt-1 text-sm font-medium text-on-surface">
                            {formatAddress(stop.address, stop.formatted_location)}
                          </p>
                          {stop.address?.notes ? (
                            <p className="mt-1 text-sm text-on-surface-variant">{stop.address.notes}</p>
                          ) : null}
                        </div>
                      ))
                    : null}
                  <div className="rounded-card bg-surface-container-low p-3">
                    <p className="text-xs font-semibold uppercase text-on-surface-variant">Dropoff</p>
                    <p className="mt-1 text-sm font-medium text-on-surface">
                      {formatAddress(reservation.tripDetails?.dropoff_address, reservation.tripDetails?.dropoff_location)}
                    </p>
                    {reservation.tripDetails?.dropoff_address?.notes ? (
                      <p className="mt-1 text-sm text-on-surface-variant">{reservation.tripDetails.dropoff_address.notes}</p>
                    ) : null}
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Timing and Passenger Notes">
                <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                  <div className="rounded-card bg-surface-container-low p-3">
                    <dt className="text-xs font-semibold uppercase text-on-surface-variant">Start</dt>
                    <dd className="mt-1 text-on-surface">{formatDateTime(reservation.startDate)}</dd>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3">
                    <dt className="text-xs font-semibold uppercase text-on-surface-variant">End</dt>
                    <dd className="mt-1 text-on-surface">{formatDateTime(reservation.endDate)}</dd>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3">
                    <dt className="text-xs font-semibold uppercase text-on-surface-variant">Passenger</dt>
                    <dd className="mt-1 text-on-surface">{reservation.tripDetails?.passenger_name || getCustomerLabel(reservation)}</dd>
                  </div>
                  <div className="rounded-card bg-surface-container-low p-3">
                    <dt className="text-xs font-semibold uppercase text-on-surface-variant">Service Notes</dt>
                    <dd className="mt-1 text-on-surface">{(reservation.tripDetails as any)?.service_notes || 'N/A'}</dd>
                  </div>
                </dl>
              </SectionCard>
            </div>
          ) : null}

	          {drawerTab === 'vehicles' ? (
	            <div className="space-y-4">
              <SectionCard
                title="Vehicle Assignments"
                subtitle="Assign exact vehicles after the quote has defined the required capacity."
                action={
                  <span className="rounded-pill bg-surface-container-high px-3 py-1 text-xs font-semibold text-on-surface">
                    {formatLabel(reservation.assignmentStatus || 'unassigned')}
                  </span>
                }
              >
                <div className="grid gap-3 sm:grid-cols-3">
                  <SummaryTile
                    label="Requirements"
                    value={
                      reservation.vehicleRequirements?.length
                        ? reservation.vehicleRequirements.length
                        : 1
                    }
                  />
                  <SummaryTile
                    label="Assigned"
                    value={vehicleAssignmentCount}
                    tone={vehicleAssignmentCount ? 'success' : 'warning'}
                  />
                  <SummaryTile
                    label="Legacy Vehicle"
                    value={reservation.vehicle?.id ? 'Present' : 'None'}
                  />
                </div>
              </SectionCard>
              {(reservation.vehicleRequirements?.length
                ? reservation.vehicleRequirements
                : [{ id: null, vehicle_label: getVehicleLabel(reservation), quantity: 1 }]
              ).map((requirement: any, index: number) => (
                <VehicleAssignmentCard
                  key={requirement.id || `manual-${index}`}
                  reservation={reservation}
                  requirement={requirement}
                  assignments={reservation.vehicleAssignments || []}
                  onChanged={refreshReservationQueries}
                />
              ))}
	            </div>
	          ) : null}

	          {drawerTab === 'drivers' ? (
	            <div className="space-y-4">
	              <SectionCard
	                title="Driver Assignments"
	                subtitle="Assign exact drivers after vehicles are known, while checking credentials, qualifications, availability, and conflicts."
	                action={
	                  <span className="rounded-pill bg-surface-container-high px-3 py-1 text-xs font-semibold text-on-surface">
	                    {formatLabel(reservation.driverAssignmentStatus || 'unassigned')}
	                  </span>
	                }
	              >
	                <div className="grid gap-3 sm:grid-cols-3">
	                  <SummaryTile
	                    label="Required"
	                    value={driverRequiredCount || 0}
	                  />
	                  <SummaryTile
	                    label="Assigned"
	                    value={driverAssignmentCount}
	                    tone={driverAssignmentCount >= (driverRequiredCount || 1) ? 'success' : driverAssignmentCount ? 'info' : 'warning'}
	                  />
	                  <SummaryTile
	                    label="Legacy Driver"
	                    value={reservation.tripDetails?.driver_name || 'None'}
	                  />
	                </div>
	                {reservation.driverReadiness?.blockingIssues?.length || reservation.driverReadiness?.warnings?.length ? (
	                  <div className="mt-4 space-y-2">
	                    {(reservation.driverReadiness?.blockingIssues || []).map((issue) => (
	                      <p key={issue} className="rounded-card bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
	                        {issue}
	                      </p>
	                    ))}
	                    {(reservation.driverReadiness?.warnings || []).map((warning) => (
	                      <p key={warning} className="rounded-card bg-surface-container-low px-3 py-2 text-sm text-on-surface-variant">
	                        {warning}
	                      </p>
	                    ))}
	                  </div>
	                ) : null}
	              </SectionCard>
	              {(reservation.vehicleAssignments?.length
	                ? reservation.vehicleAssignments
	                : [null]
	              ).map((vehicleAssignment: any, index: number) => (
	                <DriverAssignmentCard
	                  key={vehicleAssignment?.id || `reservation-driver-${index}`}
	                  reservation={reservation}
	                  vehicleAssignment={vehicleAssignment}
	                  assignments={reservation.driverAssignments || []}
	                  onChanged={refreshReservationQueries}
	                />
	              ))}
	            </div>
	          ) : null}

	          {drawerTab === 'billing' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <SummaryTile
                  label="Contract Total"
                  value={formatDollars(billingSummary.contractTotal || reservation.totalPrice)}
                />
                <SummaryTile
                  label="Deposit"
                  value={formatDollars(billingSummary.depositRequired || reservation.depositAmount)}
                  detail={`${formatDollars(billingSummary.depositPaid || 0)} received`}
                  tone={billingSummary.depositStatus === 'paid' ? 'success' : billingSummary.depositStatus === 'due' ? 'warning' : 'default'}
                />
                <SummaryTile
                  label="Balance Remaining"
                  value={formatDollars(billingSummary.balanceRemaining || reservation.totalPrice)}
                  detail={formatNextBillingAction(billingSummary.nextAction)}
                  tone={billingSummary.nextAction === 'paid_in_full' ? 'success' : 'info'}
                />
              </div>

              <SectionCard
                title="Billing Source of Truth"
                subtitle={describeDepositTiming(
                  reservation.quoteSource?.termsSnapshot
                )}
                action={
                  <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${getStatusClass(billingSummary.depositStatus === 'paid' ? 'confirmed' : billingSummary.depositStatus === 'due' ? 'pending' : 'completed')}`}>
                    {formatNextBillingAction(billingSummary.nextAction)}
                  </span>
                }
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-card border border-outline/60 bg-surface-container-low p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase text-on-surface-variant">Deposit</p>
                        <p className="mt-1 text-lg font-semibold text-on-surface">
                          {formatDollars(billingSummary.depositRequired || reservation.depositAmount)}
                        </p>
                      </div>
                      <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${getStatusClass(billingSummary.depositStatus === 'paid' ? 'confirmed' : billingSummary.depositStatus === 'due' ? 'pending' : 'completed')}`}>
                        {billingSummary.depositStatus === 'paid'
                          ? 'Received'
                          : billingSummary.depositStatus === 'due'
                          ? 'Due'
                          : 'Not required'}
                      </span>
                    </div>
                    <dl className="mt-3 space-y-2 text-sm">
                      <DetailRow label="Invoice">
                        {billingSummary.depositInvoice?.invoiceNumber ||
                          reservation.quoteSource?.depositInvoiceNumber ||
                          quoteDepositInvoice?.invoice_number ||
                          'Not created'}
                      </DetailRow>
                      <DetailRow label="Paid">
                        {formatDollars(billingSummary.depositPaid || 0)}
                      </DetailRow>
                      <DetailRow label="Payment request" mono>
                        {reservation.quoteSource?.depositPaymentRequestId || 'N/A'}
                      </DetailRow>
                    </dl>
                    {depositPaymentUrl ? (
                      <a
                        className="btn-secondary mt-4 inline-flex"
                        href={depositPaymentUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {billingSummary.depositStatus === 'paid'
                          ? 'View Deposit Record'
                          : 'Open Deposit Request'}
                      </a>
                    ) : null}
                    {canCreateDepositInvoice ? (
                      <button
                        type="button"
                        className="btn-primary mt-4"
                        disabled={createDepositInvoiceMutation.isLoading}
                        onClick={() => createDepositInvoiceMutation.mutate()}
                      >
                        {createDepositInvoiceMutation.isLoading
                          ? 'Creating...'
                          : 'Create Deposit Invoice'}
                      </button>
                    ) : null}
                  </div>

	                  <div className="rounded-card border border-outline/60 bg-surface-container-low p-4">
	                    <div className="flex items-start justify-between gap-3">
	                      <div>
	                        <p className="text-xs font-semibold uppercase text-on-surface-variant">Balance</p>
	                        <p className="mt-1 text-lg font-semibold text-on-surface">
	                          {formatDollars(billingSummary.balanceRemaining || 0)}
	                        </p>
	                      </div>
	                      <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${getStatusClass(billingSummary.balanceStatus || balanceInvoice?.status || 'pending')}`}>
	                        {formatBillingState(billingSummary.balanceStatus)}
	                      </span>
	                    </div>
	                    <dl className="mt-3 space-y-2 text-sm">
	                      <DetailRow label="Invoiced">
	                        {formatDollars(billingSummary.balanceInvoiced || 0)}
                      </DetailRow>
                      <DetailRow label="Paid">
                        {formatDollars(billingSummary.balancePaid || 0)}
                      </DetailRow>
	                      <DetailRow label="Invoice">
	                        {balanceInvoice?.invoiceNumber || 'Not created'}
	                      </DetailRow>
	                      {balanceInvoice?.status ? (
	                        <DetailRow label="Invoice status">
	                          {formatLabel(balanceInvoice.status)}
	                        </DetailRow>
	                      ) : null}
	                      {balancePaymentRequest?.id ? (
	                        <DetailRow label="Grouped request" mono>
	                          {balancePaymentRequest.id}
	                        </DetailRow>
	                      ) : null}
	                      {balancePaymentRequest?.status ? (
	                        <DetailRow label="Request status">
	                          {formatLabel(balancePaymentRequest.status)}
	                        </DetailRow>
	                      ) : null}
	                      {balanceInvoiceLink || groupedPaymentLink ? (
	                        <DetailRow label="Payment link">
	                          {groupedPaymentLink
	                            ? 'Grouped payment request'
	                            : 'Invoice payment link'}
	                        </DetailRow>
	                      ) : null}
	                    </dl>
	                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
	                      {canCreateBalanceInvoice ? (
	                        <button
	                          type="button"
	                          className="btn-primary"
	                          disabled={createBalanceInvoiceMutation.isLoading}
                          onClick={() => createBalanceInvoiceMutation.mutate()}
                        >
	                          {createBalanceInvoiceMutation.isLoading ? 'Creating...' : 'Create Balance Invoice'}
	                        </button>
	                      ) : null}
	                      {allowedBillingActions.finalizeBalanceInvoice ? (
	                        <button
	                          type="button"
	                          className="btn-primary"
	                          disabled={finalizeBalanceInvoiceMutation.isLoading}
	                          onClick={() => finalizeBalanceInvoiceMutation.mutate()}
	                        >
	                          {finalizeBalanceInvoiceMutation.isLoading ? 'Finalizing...' : 'Finalize Invoice'}
	                        </button>
	                      ) : null}
	                      {allowedBillingActions.generateInvoicePublicLink ||
	                      allowedBillingActions.copyInvoicePaymentLink ? (
	                        <button
	                          type="button"
	                          className="btn-secondary"
	                          disabled={generateInvoiceLinkMutation.isLoading}
	                          onClick={() => {
	                            if (balanceInvoiceLink) {
	                              copyTextToClipboard(balanceInvoiceLink, 'Invoice payment link copied');
	                              return;
	                            }
	                            generateInvoiceLinkMutation.mutate();
	                          }}
	                        >
	                          {generateInvoiceLinkMutation.isLoading ? 'Preparing...' : 'Copy Invoice Link'}
	                        </button>
	                      ) : null}
	                      {allowedBillingActions.sendInvoice ? (
	                        <button
	                          type="button"
	                          className="btn-secondary"
	                          disabled={sendInvoiceMutation.isLoading}
	                          onClick={() => sendInvoiceMutation.mutate()}
	                        >
	                          {sendInvoiceMutation.isLoading ? 'Sending...' : 'Send Invoice Email'}
	                        </button>
	                      ) : null}
	                      {allowedBillingActions.createBalanceGroupedRequest ? (
	                        <button
	                          type="button"
	                          className="btn-secondary"
	                          disabled={createGroupedPaymentRequestMutation.isLoading}
	                          onClick={() => createGroupedPaymentRequestMutation.mutate()}
	                        >
	                          {createGroupedPaymentRequestMutation.isLoading ? 'Creating...' : 'Create Grouped Request'}
	                        </button>
	                      ) : null}
	                      {allowedBillingActions.copyGroupedPaymentLink ? (
	                        <button
	                          type="button"
	                          className="btn-secondary"
	                          disabled={generateGroupedPaymentLinkMutation.isLoading}
	                          onClick={() => {
	                            if (groupedPaymentLink) {
	                              copyTextToClipboard(groupedPaymentLink, 'Grouped payment link copied');
	                              return;
	                            }
	                            generateGroupedPaymentLinkMutation.mutate();
	                          }}
	                        >
	                          {generateGroupedPaymentLinkMutation.isLoading ? 'Preparing...' : 'Copy Grouped Link'}
	                        </button>
	                      ) : null}
	                      {allowedBillingActions.sendGroupedPaymentLink ? (
	                        <button
	                          type="button"
	                          className="btn-secondary"
	                          disabled={sendGroupedPaymentLinkMutation.isLoading}
	                          onClick={() => sendGroupedPaymentLinkMutation.mutate()}
	                        >
	                          {sendGroupedPaymentLinkMutation.isLoading ? 'Sending...' : 'Send Grouped Email'}
	                        </button>
	                      ) : null}
		                      {allowedBillingActions.markGroupedPaymentPaid ? (
		                        <button
		                          type="button"
		                          className="btn-secondary"
		                          disabled={manualPaymentSaving}
		                          onClick={() => openManualPaymentPanel('grouped_request')}
		                        >
		                          Mark Grouped Paid
		                        </button>
		                      ) : null}
		                      {allowedBillingActions.markBalanceInvoicePaid ? (
		                        <button
		                          type="button"
		                          className="btn-secondary"
		                          disabled={manualPaymentSaving}
		                          onClick={() => openManualPaymentPanel('balance_invoice')}
		                        >
		                          Mark Invoice Paid
		                        </button>
		                      ) : null}
	                      {balanceInvoice ? (
	                        <a
	                          className="btn-secondary inline-flex justify-center"
	                          href={`/admin/invoices?search=${encodeURIComponent(balanceInvoice.invoiceNumber || balanceInvoice.id)}`}
	                        >
	                          Open Invoice
	                        </a>
	                      ) : null}
	                    </div>
	                    {manualPaymentTarget ? (
	                      <div className="mt-4 rounded-card border border-primary/20 bg-primary/5 p-4">
	                        <div className="flex items-start justify-between gap-3">
	                          <div>
	                            <p className="text-sm font-semibold text-on-surface">
	                              Record Manual Payment
	                            </p>
	                            <p className="mt-1 text-xs text-on-surface-variant">
	                              {manualPaymentTarget === 'grouped_request'
	                                ? 'Mark the grouped payment request as paid by check, ACH, wire, cash, or another offline method.'
	                                : 'Mark the balance invoice as paid by check, ACH, wire, cash, or another offline method.'}
	                            </p>
	                          </div>
	                          <button
	                            type="button"
	                            className="text-sm font-medium text-primary"
	                            disabled={manualPaymentSaving}
	                            onClick={() => setManualPaymentTarget(null)}
	                          >
	                            Cancel
	                          </button>
	                        </div>
	                        <div className="mt-3 grid gap-3 sm:grid-cols-[180px_1fr]">
	                          <label className="form-field">
	                            <span className="field-label">Method</span>
	                            <select
	                              className="form-input"
	                              value={manualPaymentForm.paymentMethod}
	                              disabled={manualPaymentSaving}
	                              onChange={(event) =>
	                                setManualPaymentForm((current) => ({
	                                  ...current,
	                                  paymentMethod: event.target.value,
	                                }))
	                              }
	                            >
	                              <option value="check">Check</option>
	                              <option value="ach">ACH</option>
	                              <option value="wire">Wire</option>
	                              <option value="cash">Cash</option>
	                              <option value="other">Other</option>
	                            </select>
	                          </label>
	                          <label className="form-field">
	                            <span className="field-label">Note</span>
	                            <input
	                              className="form-input"
	                              value={manualPaymentForm.note || ''}
	                              disabled={manualPaymentSaving}
	                              placeholder="Check number, reference, or internal note"
	                              onChange={(event) =>
	                                setManualPaymentForm((current) => ({
	                                  ...current,
	                                  note: event.target.value,
	                                }))
	                              }
	                            />
	                          </label>
	                        </div>
	                        <div className="mt-3 flex flex-wrap justify-end gap-2">
	                          <button
	                            type="button"
	                            className="btn-secondary"
	                            disabled={manualPaymentSaving}
	                            onClick={() => setManualPaymentTarget(null)}
	                          >
	                            Cancel
	                          </button>
	                          <button
	                            type="button"
	                            className="btn-primary"
	                            disabled={manualPaymentSaving}
	                            onClick={submitManualPayment}
	                          >
	                            {manualPaymentSaving ? 'Saving...' : 'Record Payment'}
	                          </button>
	                        </div>
	                      </div>
	                    ) : null}
	                  </div>
	                </div>
	              </SectionCard>

              <SectionCard title="Reservation Invoices">
                {billingInvoices.length === 0 ? (
                  <p className="text-sm text-on-surface-variant">
                    No invoices are attached to this reservation yet.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-card border border-outline/60">
                    <table className="min-w-full divide-y divide-outline/60 text-sm">
                      <thead className="bg-surface-container-low">
                        <tr className="text-left text-xs font-semibold uppercase text-on-surface-variant">
                          <th className="px-3 py-2">Invoice</th>
                          <th className="px-3 py-2">Kind</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2 text-right">Paid</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline/60">
                        {billingInvoices.map((invoice) => (
                          <tr key={invoice.id} className="bg-surface-container-lowest">
                            <td className="px-3 py-2 font-medium text-on-surface">
                              {invoice.invoiceNumber || invoice.id}
                            </td>
                            <td className="px-3 py-2 text-on-surface-variant">
                              {formatLabel(invoice.kind)}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`rounded-pill px-2 py-1 text-xs font-semibold ${getStatusClass(invoice.status)}`}>
                                {formatLabel(invoice.status)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-on-surface">
                              {formatDollars(invoice.total || 0)}
                            </td>
                            <td className="px-3 py-2 text-right text-on-surface-variant">
                              {formatDollars(invoice.amountPaid || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </div>
          ) : null}

          {drawerTab === 'invoices' ? (
            <div className="space-y-4">
              <RecordInvoicesPanel
                contextType="reservation"
                invoices={relatedInvoices}
                loading={isLoading}
                emptyTitle="No invoices attached to this reservation yet"
                emptyDescription="Create a reservation invoice so billing, customer, account, and trip lineage remain attached to this record."
                onCreateInvoice={() => setShowCreateInvoice(true)}
                onOpenInvoice={(invoice) => setSelectedFullInvoice(invoice)}
                onRefresh={refreshReservationQueries}
              />

              <SectionCard title="Attach Existing Invoice" subtitle="Use this when an invoice was created elsewhere and should roll up to this reservation.">
                <div className="rounded-card bg-surface-container-low p-4">
                <p className="text-sm font-medium text-on-surface">
                  Attach Invoice
                </p>
                <p className="mt-1 text-sm text-on-surface-variant">
                  Attach an existing invoice to this reservation for clearer
                  accounting rollups.
                </p>
                <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                  <AsyncTypeahead
                    value={selectedInvoice}
                    onChange={(option) =>
                      setSelectedInvoice(option as InvoiceSearchOption | null)
                    }
                    loadOptions={loadInvoiceOptions}
                    placeholder="Search invoice by number, customer, or company..."
                    emptyMessage="No attachable invoices matched your search."
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={
                      !selectedInvoice || attachInvoiceMutation.isLoading
                    }
                    onClick={() => {
                      if (selectedInvoice) {
                        attachInvoiceMutation.mutate(selectedInvoice.invoiceId);
                      }
                    }}
                  >
                    {attachInvoiceMutation.isLoading
                      ? 'Attaching...'
                      : 'Attach Invoice'}
                  </button>
                </div>
                {relatedInvoices.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {relatedInvoices.map((invoice) => (
                      <span
                        key={`attached-${invoice.id}`}
                        className="inline-flex items-center gap-2 rounded-pill border border-outline/50 bg-surface px-3 py-1.5 text-xs text-on-surface-variant"
                      >
                        <span className="font-medium text-on-surface">
                          {invoice.invoice_number || 'Invoice'}
                        </span>
                        <button
                          type="button"
                          className="text-danger hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={detachInvoiceMutation.isLoading}
                          onClick={() => detachInvoiceMutation.mutate(invoice.id)}
                        >
                          Remove
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              </SectionCard>
            </div>
          ) : null}

          {drawerTab === 'payments' ? (
            <SectionCard title="Payment Activity">
              {relatedPayments.length === 0 ? (
                <p className="mt-3 text-sm text-on-surface-variant">
                  No completed payments are attached to this reservation yet.
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  {relatedPayments.map((payment) => (
                    <div
                      key={`payment-${payment.id}`}
                      className="rounded-card border border-ghost p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-on-surface">
                            Payment for {payment.invoice_number || 'invoice'}
                          </p>
                          <p className="mt-1 text-xs text-on-surface-variant">
                            Paid {formatDateTime(payment.paid_at)}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-green-700">
                          {formatCents(payment.amount_paid)}
                        </p>
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-on-surface-variant">
                        <p>
                          Payment Intent: {payment.payment_intent_id || 'N/A'}
                        </p>
                        <p>
                          Stripe Invoice: {payment.stripe_invoice_id || 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          ) : null}
        </div>
      ) : (
        <div className="py-12 text-center text-sm text-on-surface-variant">
          No reservation selected.
        </div>
      )}
      {showCreateInvoice ? (
        <CreateInvoiceModal
          onClose={() => setShowCreateInvoice(false)}
          initialCustomer={initialInvoiceCustomer}
          initialAccount={initialInvoiceAccount}
          initialReservation={initialInvoiceReservation}
          subtitle="Create an invoice with this reservation, customer, and account context prefilled."
          onSuccess={(invoice) => {
            setShowCreateInvoice(false);
            refreshReservationQueries();
            queryClient.invalidateQueries('admin-invoices');
            if (invoice) {
              setSelectedFullInvoice(invoice);
            }
          }}
        />
      ) : null}
      <InvoiceDrawerHost
        open={Boolean(selectedFullInvoice)}
        invoice={selectedFullInvoice}
        onClose={() => setSelectedFullInvoice(null)}
        onInvoiceUpdated={() => {
          refreshReservationQueries();
          queryClient.invalidateQueries('admin-invoices');
        }}
      />
    </EntityDrawer>
  );
}
